import React from "react";
import {
  Mail,
  RefreshCcw,
  FileWarning,
  FolderInput,
  CalendarClock,
  Truck,
  EyeOff,
  X,
  Check,
  Layers,
} from "lucide-react";
import { Reveal, Overline } from "./primitives";

const fragmented = [
  { icon: Mail, label: "Endless email chains" },
  { icon: RefreshCcw, label: "Quotation follow-ups" },
  { icon: FileWarning, label: "Purchase order management" },
  { icon: FolderInput, label: "Document collection" },
  { icon: CalendarClock, label: "Inspection scheduling" },
  { icon: Truck, label: "Freight coordination" },
  { icon: EyeOff, label: "Shipment visibility" },
];

const unified = [
  "Quotations & purchase orders in one place",
  "Production tracked in real time",
  "Inspections scheduled inside the Workspace",
  "FreightIQ™ booking connected end-to-end",
  "Every document centralized & versioned",
  "Live shipment milestones & alerts",
  "One source of truth for the whole operation",
];

export const ProblemSection = () => {
  return (
    <section
      id="problem"
      data-testid="problem-section"
      className="border-t border-slate-100 bg-white py-24 lg:py-32"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <Reveal className="mx-auto max-w-3xl text-center">
          <Overline>The Problem</Overline>
          <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-ink md:text-4xl">
            Managing full container purchases shouldn&apos;t be complicated.
          </h2>
          <p className="mt-4 text-base text-slateink md:text-lg">
            Traditional imports scatter your operation across inboxes, spreadsheets and phone calls.
            FullContainer™ brings every stage into one connected Workspace.
          </p>
        </Reveal>

        <div className="mt-16 grid gap-6 lg:grid-cols-2">
          {/* fragmented */}
          <Reveal>
            <div className="h-full rounded-2xl border border-slate-200 bg-slate-50/60 p-8">
              <div className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white text-slate-400 shadow-card-base">
                  <Layers className="h-4 w-4" />
                </span>
                <span className="text-sm font-semibold text-slateink">
                  Fragmented operations
                </span>
              </div>
              <div className="mt-6 space-y-2.5">
                {fragmented.map((f) => (
                  <div
                    key={f.label}
                    className="flex items-center gap-3 rounded-lg border border-slate-200/70 bg-white/70 px-4 py-3"
                  >
                    <span className="flex h-6 w-6 items-center justify-center rounded-md bg-slate-100 text-slate-400">
                      <f.icon className="h-3.5 w-3.5" strokeWidth={1.75} />
                    </span>
                    <span className="text-sm font-medium text-slateink">{f.label}</span>
                    <X className="ml-auto h-4 w-4 text-slate-300" />
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          {/* unified */}
          <Reveal delay={0.1}>
            <div className="relative h-full overflow-hidden rounded-2xl border border-demax/20 bg-white p-8 shadow-card-hover">
              <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-demax-soft blur-2xl" />
              <div className="relative flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-demax text-white shadow-btn">
                  <Layers className="h-4 w-4" />
                </span>
                <span className="text-sm font-semibold text-ink">One connected Workspace</span>
              </div>
              <div className="relative mt-6 space-y-2.5">
                {unified.map((u) => (
                  <div
                    key={u}
                    className="flex items-center gap-3 rounded-lg border border-slate-100 bg-white px-4 py-3 shadow-card-base"
                  >
                    <span className="flex h-6 w-6 items-center justify-center rounded-md bg-[#ECFDF5] text-[#059669]">
                      <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
                    </span>
                    <span className="text-sm font-medium text-ink">{u}</span>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
